import React, { forwardRef, useEffect, useRef, useState } from 'react';

// Count non-comma chars before `end` in `str`
const countDigits = (str: string, end: number) =>
  str.slice(0, end).replace(/,/g, '').length;

// Find position in `formatted` after `digits` non-comma chars
const findPos = (formatted: string, digits: number) => {
  let d = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (d === digits) return i;
    if (formatted[i] !== ',') d++;
  }
  return formatted.length;
};

// iOS Safari scrolls a focused input's nearest scrollable ancestor into view
// as a side effect of setSelectionRange(), even when it's already visible
// (see https://github.com/mui/mui-x/issues/8321). Capture/restore that
// ancestor's scrollTop around the call to neutralize it. Falls back to the
// document's scrolling element when there's no scrollable ancestor, since
// iOS then scrolls the page itself.
const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  let node = element?.parentElement ?? null;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return document.scrollingElement as HTMLElement | null;
};

const ControlledInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>((props, forwardedRef) => {
  const { value, onChange, ...rest } = props;
  const [cursor, setCursor] = useState<number | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const input = ref.current;
    if (!input || cursor === null || document.activeElement !== input) return;
    const pos = findPos(String(value ?? ''), cursor);
    const scrollParent = getScrollParent(input);
    const scrollTop = scrollParent?.scrollTop;
    input.setSelectionRange(pos, pos);
    if (scrollParent && scrollTop !== undefined) {
      scrollParent.scrollTop = scrollTop;
    }
  }, [ref, cursor, value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCursor(countDigits(e.target.value, e.target.selectionStart ?? 0));
    onChange && onChange(e);
  };

  return (
    <input
      ref={(el) => {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
        if (typeof forwardedRef === 'function') forwardedRef(el);
        else if (forwardedRef) forwardedRef.current = el;
      }}
      value={value}
      onChange={handleChange}
      {...rest}
    />
  );
});

ControlledInput.displayName = 'ControlledInput';

export default ControlledInput;
